import { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import {
  getNotifications,
  markNotificationsRead,
  respondRequest,
  getRequests,
  api,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Bell,
  Heart,
  Users,
  MessageCircle,
  Check,
  X,
  UserRound,
  ChevronRight,
  Clock3,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import ProfileModal from "@/components/ProfileModal";

const NOTIFICATION_CONFIG = {
  dating_request: {
    icon: Heart,
    iconClass: "text-rose-600",
    iconBg: "bg-rose-50",
    label: "Dating request",
  },
  friend_request: {
    icon: Users,
    iconClass: "text-blue-600",
    iconBg: "bg-blue-50",
    label: "Friend request",
  },
  dating_accepted: {
    icon: Check,
    iconClass: "text-emerald-600",
    iconBg: "bg-emerald-50",
    label: "Dating request accepted",
  },
  friend_accepted: {
    icon: Check,
    iconClass: "text-blue-600",
    iconBg: "bg-blue-50",
    label: "Friend request accepted",
  },
  match_message: {
    icon: MessageCircle,
    iconClass: "text-violet-600",
    iconBg: "bg-violet-50",
    label: "New message",
  },
  match_new: {
    icon: Heart,
    iconClass: "text-rose-600",
    iconBg: "bg-rose-50",
    label: "New connection",
  },
};

export default function Notifications() {
  const { user } = useAuth();

  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const [responding, setResponding] = useState({});

  const loadNotifs = useCallback(async () => {
    if (!user?.user_id) return;

    setLoading(true);

    try {
      const data = await getNotifications();
      const safeData = Array.isArray(data) ? data : [];

      const sorted = [...safeData].sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime()
      );

      setNotifs(sorted);

      // Only PATCH if there's anything unread.
      if (sorted.some((n) => n.read !== true)) {
        await markNotificationsRead();
      }
    } catch (err) {
      console.error("Failed to load notifications:", err);
      toast.error("Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    loadNotifs();
  }, [loadNotifs]);

  const handleViewProfile = useCallback(async (userId) => {
    if (!userId || userId === "system") return;

    try {
      const res = await api.get(`/users/${userId}/profile`);

      if (res?.data) {
        setSelectedProfile(res.data);
      }
    } catch (err) {
      console.error("Failed to load notification profile:", err);
      toast.error("Could not load profile");
    }
  }, []);

  const handleRespond = useCallback(
    async (notification, action) => {
      if (!notification?.notification_id) return;

      const notificationKey = notification.notification_id;

      if (responding[notificationKey]) return;

      setResponding((prev) => ({
        ...prev,
        [notificationKey]: true,
      }));

      try {
        const allRequests = await getRequests();
        const requests = Array.isArray(allRequests) ? allRequests : [];

        const fromUserId = notification.from_user_id;

        let expectedType = null;

        if (notification.type === "dating_request") {
          expectedType = "dating";
        } else if (notification.type === "friend_request") {
          expectedType = "friend";
        }

        const request = requests.find(
          (item) =>
            item.type === expectedType &&
            item.from_user_id === fromUserId
        );

        if (!request?.request_id) {
          toast.error(
            "This request is no longer available. Refreshing notifications..."
          );
          await loadNotifs();
          return;
        }

        await respondRequest(request.request_id, action);

        setNotifs((prev) =>
          prev.filter(
            (item) => item.notification_id !== notification.notification_id
          )
        );

        toast.success(
          action === "accept"
            ? expectedType === "dating"
              ? "Dating request accepted"
              : "Friend request accepted"
            : "Request declined"
        );

        await loadNotifs();
      } catch (err) {
        console.error("Failed to respond to notification request:", err);
        toast.error("Failed to respond to request");
      } finally {
        setResponding((prev) => ({
          ...prev,
          [notificationKey]: false,
        }));
      }
    },
    [loadNotifs, responding]
  );

  const handleDeleteNotification = useCallback(async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifs((prev) =>
        prev.filter((item) => item.notification_id !== notificationId)
      );
      toast.success("Notification removed");
    } catch (err) {
      console.error("Failed to delete notification:", err);
      toast.error("Could not remove notification");
    }
  }, []);

  const timeAgo = useCallback((date) => {
    if (!date) return "";

    const timestamp = new Date(date).getTime();

    if (Number.isNaN(timestamp)) return "";

    const seconds = Math.max(
      0,
      Math.floor((Date.now() - timestamp) / 1000)
    );

    if (seconds < 10) return "Just now";

    if (seconds < 60) {
      return `${seconds}s ago`;
    }

    const minutes = Math.floor(seconds / 60);

    if (minutes < 60) {
      return `${minutes}m ago`;
    }

    const hours = Math.floor(minutes / 60);

    if (hours < 24) {
      return `${hours}h ago`;
    }

    const days = Math.floor(hours / 24);

    if (days < 7) {
      return `${days}d ago`;
    }

    const weeks = Math.floor(days / 7);

    if (weeks < 5) {
      return `${weeks}w ago`;
    }

    return new Date(date).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year:
        new Date(date).getFullYear() !== new Date().getFullYear()
          ? "numeric"
          : undefined,
    });
  }, []);

  const groupedNotifications = useMemo(() => {
    const now = new Date();

    const today = [];
    const earlier = [];

    notifs.forEach((notification) => {
      const date = new Date(notification.created_at);

      const sameDay =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

      if (sameDay) {
        today.push(notification);
      } else {
        earlier.push(notification);
      }
    });

    return { today, earlier };
  }, [notifs]);

  const unreadCount = useMemo(
    () => notifs.filter((notification) => !notification.read).length,
    [notifs]
  );

  const renderNotification = (notification) => {
    const fromUserId = notification.from_user_id;
    const isSystem = fromUserId === "system";

    const isRequest =
      notification.type === "dating_request" ||
      notification.type === "friend_request";

    const config =
      NOTIFICATION_CONFIG[notification.type] || {
        icon: Bell,
        iconClass: "text-gray-500",
        iconBg: "bg-gray-100",
        label: "Notification",
      };

    const Icon = config.icon;
    const isResponding = Boolean(responding[notification.notification_id]);

    return (
      <article
        key={notification.notification_id}
        className={`
          group relative
          bg-white
          border
          rounded-2xl
          overflow-hidden
          transition-all duration-200
          ${
            !notification.read
              ? "border-purple-200 shadow-sm"
              : "border-[#E7E5E0]"
          }
          hover:shadow-md
          hover:border-gray-300
        `}
      >
        {!notification.read && (
          <span
            className="absolute left-0 top-0 bottom-0 w-1 bg-purple-600"
            aria-hidden="true"
          />
        )}

        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3.5">
            <button
              type="button"
              onClick={() => {
                if (!isSystem) handleViewProfile(fromUserId);
              }}
              disabled={isSystem}
              className={`
                relative
                w-12 h-12
                sm:w-13 sm:h-13
                rounded-full
                overflow-hidden
                flex-shrink-0
                ring-1 ring-black/5
                ${
                  !isSystem
                    ? "cursor-pointer hover:ring-2 hover:ring-purple-300"
                    : "cursor-default"
                }
                transition-all
              `}
              aria-label={
                isSystem
                  ? "System notification"
                  : "View profile"
              }
            >
              {notification.from_image ? (
                <img
                  src={notification.from_image}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div
                  className={`
                    w-full h-full
                    flex items-center justify-center
                    ${config.iconBg}
                  `}
                >
                  <Icon
                    className={`w-5 h-5 ${config.iconClass}`}
                  />
                </div>
              )}

              {!isSystem && (
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" />
              )}
            </button>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`
                        inline-flex items-center
                        px-2 py-0.5
                        rounded-full
                        text-[10px]
                        sm:text-[11px]
                        font-semibold
                        uppercase
                        tracking-wide
                        ${config.iconBg}
                        ${config.iconClass}
                      `}
                    >
                      {config.label}
                    </span>

                    {!notification.read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-600" />
                    )}
                  </div>

                  <button
                    type="button"
                    disabled={isSystem}
                    onClick={() => {
                      if (!isSystem) {
                        handleViewProfile(fromUserId);
                      }
                    }}
                    className={`
                      mt-2
                      text-left
                      w-full
                      ${
                        !isSystem
                          ? "cursor-pointer"
                          : "cursor-default"
                      }
                    `}
                  >
                    <p
                      className={`
                        text-sm sm:text-[15px]
                        leading-6
                        ${
                          !notification.read
                            ? "font-semibold text-gray-900"
                            : "font-medium text-gray-800"
                        }
                      `}
                    >
                      {notification.message}
                    </p>
                  </button>

                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-400">
                    <Clock3 className="w-3.5 h-3.5" />
                    <span>{timeAgo(notification.created_at)}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    handleDeleteNotification(
                      notification.notification_id
                    )
                  }
                  className="
                    p-1.5
                    rounded-full
                    text-gray-300
                    hover:text-gray-500
                    hover:bg-gray-100
                    opacity-0
                    group-hover:opacity-100
                    focus:opacity-100
                    transition-all
                    flex-shrink-0
                  "
                  title="Remove notification"
                  aria-label="Remove notification"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {isRequest && (
                <div className="mt-4 pt-3.5 border-t border-gray-100">
                  <div className="flex flex-col xs:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        handleRespond(notification, "accept")
                      }
                      disabled={isResponding}
                      className="
                        flex-1
                        min-h-[42px]
                        inline-flex items-center justify-center gap-2
                        px-4 py-2.5
                        rounded-xl
                        bg-purple-600
                        text-white
                        text-sm
                        font-semibold
                        hover:bg-purple-700
                        active:bg-purple-800
                        disabled:opacity-60
                        disabled:cursor-not-allowed
                        transition-colors
                      "
                    >
                      {isResponding ? (
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}

                      {isResponding
                        ? "Processing..."
                        : "Accept"}
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        handleRespond(notification, "reject")
                      }
                      disabled={isResponding}
                      className="
                        flex-1
                        min-h-[42px]
                        inline-flex items-center justify-center gap-2
                        px-4 py-2.5
                        rounded-xl
                        bg-gray-100
                        text-gray-700
                        text-sm
                        font-semibold
                        hover:bg-gray-200
                        active:bg-gray-300
                        disabled:opacity-60
                        disabled:cursor-not-allowed
                        transition-colors
                      "
                    >
                      <X className="w-4 h-4" />
                      Decline
                    </button>
                  </div>
                </div>
              )}

              {!isSystem && !isRequest && (
                <button
                  type="button"
                  onClick={() => handleViewProfile(fromUserId)}
                  className="
                    mt-3
                    inline-flex items-center gap-1
                    text-xs font-semibold
                    text-purple-600
                    hover:text-purple-700
                  "
                >
                  View profile
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />

      <main className="w-full max-w-2xl mx-auto px-3 sm:px-5 py-5 sm:py-8 pb-12">
        <header className="mb-6 sm:mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-purple-600" />
                </div>

                <div>
                  <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900">
                    Notifications
                  </h1>

                  <p className="text-sm text-gray-500 mt-0.5">
                    Stay up to date with your connections
                  </p>
                </div>
              </div>
            </div>

            {unreadCount > 0 && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100">
                <span className="w-2 h-2 rounded-full bg-purple-600" />
                <span className="text-xs font-semibold text-purple-700">
                  {unreadCount} new
                </span>
              </div>
            )}
          </div>
        </header>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="bg-white border border-[#E7E5E0] rounded-2xl p-5 animate-pulse"
              >
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex-shrink-0" />

                  <div className="flex-1 space-y-3">
                    <div className="h-3 bg-gray-200 rounded-full w-24" />
                    <div className="h-4 bg-gray-200 rounded-full w-4/5" />
                    <div className="h-3 bg-gray-200 rounded-full w-20" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : notifs.length === 0 ? (
          <div className="bg-white border border-[#E7E5E0] rounded-3xl px-6 py-16 sm:py-20 text-center shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-5">
              <Bell className="w-8 h-8 text-purple-400" />
            </div>

            <h2 className="font-display text-lg sm:text-xl font-semibold text-gray-900">
              You're all caught up
            </h2>

            <p className="max-w-sm mx-auto text-sm text-gray-500 mt-2 leading-6">
              New dating requests, friend requests, messages, and
              connections will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-7">
            {groupedNotifications.today.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-3 px-1">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Today
                  </h2>

                  <div className="h-px flex-1 bg-gray-200" />
                </div>

                <div className="space-y-2.5">
                  {groupedNotifications.today.map(
                    renderNotification
                  )}
                </div>
              </section>
            )}

            {groupedNotifications.earlier.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-3 px-1">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Earlier
                  </h2>

                  <div className="h-px flex-1 bg-gray-200" />
                </div>

                <div className="space-y-2.5">
                  {groupedNotifications.earlier.map(
                    renderNotification
                  )}
                </div>
              </section>
            )}
          </div>
        )}

        {!loading && notifs.length > 0 && (
          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-400">
            <ShieldCheck className="w-4 h-4" />
            <span>Your notifications are private</span>
            <Sparkles className="w-3.5 h-3.5" />
          </div>
        )}
      </main>

      {selectedProfile && (
        <ProfileModal
          profile={selectedProfile}
          onClose={() => setSelectedProfile(null)}
        />
      )}
    </div>
  );
}